import { useMemo, useState } from 'react'
import { TopBar } from '../components/TopBar'
import { Sheet } from '../components/Sheet'
import { Avatar } from '../components/Avatar'
import { EventCard } from '../components/EventCard'
import { useApp } from '../store/AppContext'
import { localISODate } from '../lib/date'
import { typeMeta } from '../lib/eventMeta'
import { datesNeedingCover } from '../lib/cover'
import { adultAlexEvents } from '../lib/adultAlexScope'
import type { EventType, ScheduleEvent } from '../types'

const toISO = localISODate

function buildWeek(centerISO: string) {
  const center = new Date(centerISO + 'T00:00:00')
  const start = new Date(center)
  start.setDate(center.getDate() - center.getDay())
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return d
  })
}

function buildMonth(anchorISO: string) {
  const anchor = new Date(anchorISO + 'T00:00:00')
  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  const gridStart = new Date(firstOfMonth)
  gridStart.setDate(firstOfMonth.getDate() - firstOfMonth.getDay())

  const lastOfMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)
  const gridEnd = new Date(lastOfMonth)
  gridEnd.setDate(lastOfMonth.getDate() + (6 - lastOfMonth.getDay()))

  const days: { date: Date; inMonth: boolean }[] = []
  const cursor = new Date(gridStart)
  while (cursor <= gridEnd) {
    days.push({ date: new Date(cursor), inMonth: cursor.getMonth() === anchor.getMonth() })
    cursor.setDate(cursor.getDate() + 1)
  }
  return days
}

export function CalendarPage() {
  const { currentUser, events, addEvent, availableTeamMembersOn } = useApp()
  const [selected, setSelected] = useState(toISO(new Date()))
  const [view, setView] = useState<'week' | 'month'>('week')
  const [showAdd, setShowAdd] = useState(false)
  const canSchedule = ['participant', 'rep', 'coordinator'].includes(currentUser.role)

  const week = useMemo(() => buildWeek(selected), [selected])
  const month = useMemo(() => buildMonth(selected), [selected])
  const adultEvents = useMemo(() => adultAlexEvents(events).filter(event => event.calendarAudience !== 'family'), [events])

  const eventsForRole = (list: ScheduleEvent[]) =>
    currentUser.role === 'support_worker'
      ? list.filter((e) => e.assignees.some((a) => a.userId === currentUser.id))
      : list

  const visibleEvents = eventsForRole(adultEvents)

  const dayEvents = visibleEvents
    .filter((e) => e.date === selected)
    .sort((a, b) => a.startTime.localeCompare(b.startTime))

  const eventTypesByDate = useMemo(() => {
    const map = new Map<string, Set<EventType>>()
    eventsForRole(adultEvents).forEach((e) => {
      if (!map.has(e.date)) map.set(e.date, new Set())
      map.get(e.date)!.add(e.type)
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adultEvents, currentUser])

  const coverDates = useMemo(
    () => datesNeedingCover(visibleEvents),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adultEvents, currentUser],
  )

  const hasAvailableTeamMember = (iso: string) => availableTeamMembersOn(iso).length > 0

  const availableToday = availableTeamMembersOn(selected)
  const daysNeedingCover = dayEvents.filter((e) => coverDates.has(e.date) && e.cover?.status === 'needs_cover')

  const shiftMonth = (delta: number) => {
    const d = new Date(selected + 'T00:00:00')
    d.setMonth(d.getMonth() + delta, 1)
    setSelected(toISO(d))
  }

  return (
    <div>
      <TopBar title="Calendar" subtitle="Everyone's schedule in one place" />

      <div className="border-b border-slate-100 bg-white px-4 py-3">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex rounded-xl bg-slate-100 p-1 text-xs font-semibold">
            <button
              onClick={() => setView('week')}
              className={`rounded-lg px-3 py-1.5 ${view === 'week' ? 'bg-white text-brand-700 shadow-card' : 'text-slate-500'}`}
            >
              Week
            </button>
            <button
              onClick={() => setView('month')}
              className={`rounded-lg px-3 py-1.5 ${view === 'month' ? 'bg-white text-brand-700 shadow-card' : 'text-slate-500'}`}
            >
              Month
            </button>
          </div>
          {view === 'month' && (
            <div className="flex items-center gap-2">
              <button onClick={() => shiftMonth(-1)} className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-500">‹</button>
              <span className="w-24 text-center text-xs font-semibold text-slate-700">
                {new Date(selected + 'T00:00:00').toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
              </span>
              <button onClick={() => shiftMonth(1)} className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-500">›</button>
            </div>
          )}
        </div>

        {view === 'week' ? (
          <div className="flex items-center justify-between">
            {week.map((d) => {
              const iso = toISO(d)
              const isSelected = iso === selected
              const isToday = iso === toISO(new Date())
              const teamAvailable = hasAvailableTeamMember(iso)
              const dayNeedsCover = coverDates.has(iso)
              return (
                <button
                  key={iso}
                  onClick={() => setSelected(iso)}
                  className={`flex h-16 w-9 flex-col items-center justify-center rounded-xl text-xs font-semibold ${isSelected ? 'bg-brand-600 text-white' : isToday ? 'bg-brand-50 text-brand-700' : 'text-slate-500'}`}
                >
                  <span className="text-[10px] uppercase opacity-80">{d.toLocaleDateString(undefined, { weekday: 'narrow' })}</span>
                  <span className="text-sm">{d.getDate()}</span>
                  <span className="mt-0.5 flex h-2 items-center gap-0.5">
                    {teamAvailable && <span title="Someone on the team is available" className={`h-1.5 w-1.5 rounded-full bg-emerald-500 ${isSelected ? 'ring-1 ring-white' : ''}`} />}
                    {dayNeedsCover && <span title="A shift needs cover" className={`h-1.5 w-1.5 rounded-full bg-rose-500 ${isSelected ? 'ring-1 ring-white' : ''}`} />}
                  </span>
                </button>
              )
            })}
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-7 text-center text-[10px] font-semibold uppercase text-slate-400">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <span key={i}>{d}</span>)}
            </div>
            <div className="mt-1 grid grid-cols-7 gap-y-1">
              {month.map(({ date, inMonth }) => {
                const iso = toISO(date)
                const isSelected = iso === selected
                const isToday = iso === toISO(new Date())
                const types = Array.from(eventTypesByDate.get(iso) ?? [])
                const teamAvailable = hasAvailableTeamMember(iso)
                const dayNeedsCover = coverDates.has(iso)
                return (
                  <button key={iso} onClick={() => setSelected(iso)} className="flex flex-col items-center gap-0.5 py-1">
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${isSelected ? 'bg-brand-600 text-white' : isToday ? 'bg-brand-50 text-brand-700' : inMonth ? 'text-slate-700' : 'text-slate-300'} ${dayNeedsCover && !isSelected ? 'ring-2 ring-rose-400' : ''}`}>{date.getDate()}</span>
                    <span className="flex h-1.5 gap-0.5">
                      {teamAvailable && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                      {dayNeedsCover && <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />}
                      {types.slice(0, 2).map((t) => <span key={t} className={`h-1.5 w-1.5 rounded-full ${typeMeta[t].dot}`} />)}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        <div className="mt-2 flex items-center justify-center gap-3 text-[10px] font-medium text-slate-400">
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Team member available</span>
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Needs cover</span>
        </div>
      </div>

      <div className="px-4 py-4">
        <p className="mb-3 text-sm font-semibold text-slate-700">
          {new Date(selected + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>

        {daysNeedingCover.length > 0 && (
          <div className="mb-3 rounded-2xl border border-rose-200 bg-rose-50 px-3.5 py-3">
            <p className="text-sm font-bold text-rose-800">⚠️ {daysNeedingCover.length} shift{daysNeedingCover.length > 1 ? 's' : ''} on this day need{daysNeedingCover.length > 1 ? '' : 's'} cover</p>
            <p className="mt-0.5 text-xs text-rose-700">Open the shift below to see who on your team is available and ask them.</p>
          </div>
        )}

        {availableToday.length > 0 && (
          <div className="mb-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-3 py-2.5">
            <div className="flex items-center gap-2 overflow-x-auto">
              <span className="shrink-0 text-xs font-semibold text-emerald-700">Available:</span>
              {availableToday.map((u) => (
                <div key={u.id} className="flex shrink-0 items-center gap-1.5">
                  <div className="relative"><Avatar user={u} size={24} /><span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-emerald-50 bg-emerald-500" /></div>
                  <span className="text-xs font-medium text-emerald-800">{u.name.split(' ')[0]}</span>
                </div>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-emerald-700">These team members said they may be free — they're not booked in unless you ask and they accept.</p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          {dayEvents.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400">No events this day.</div>}
          {dayEvents.map((ev) => <EventCard key={ev.id} event={ev} />)}
        </div>

        {canSchedule && <button onClick={() => setShowAdd(true)} className="mt-4 w-full rounded-2xl bg-brand-600 py-3 text-sm font-bold text-white shadow-card">+ Add & Assign Event</button>}
      </div>

      <Sheet open={showAdd} onClose={() => setShowAdd(false)} title="New calendar event">
        <AddEventForm defaultDate={selected} onClose={() => setShowAdd(false)} onSave={(ev) => { addEvent(ev); setShowAdd(false) }} />
      </Sheet>
    </div>
  )
}

function AddEventForm({ defaultDate, onClose, onSave }: { defaultDate: string; onClose: () => void; onSave: (ev: ScheduleEvent) => void }) {
  const { currentUser, allUsers } = useApp()
  const assignable = allUsers.filter((u) => u.id !== currentUser.id)

  const [title, setTitle] = useState('')
  const [type, setType] = useState<EventType>('shift')
  const [date, setDate] = useState(defaultDate)
  const [startTime, setStartTime] = useState('09:00')
  const [endTime, setEndTime] = useState('10:00')
  const [assigneeIds, setAssigneeIds] = useState<string[]>(assignable[0] ? [assignable[0].id] : [])
  const [notes, setNotes] = useState('')

  const toggleAssignee = (userId: string) => {
    setAssigneeIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))
  }

  const submit = () => {
    if (!title.trim()) return
    if (type === 'shift' && assigneeIds.length !== 1) return
    onSave({
      id: `ev-${Date.now()}`,
      title: title.trim(),
      type,
      date,
      startTime,
      endTime,
      assignees: assigneeIds.map((userId) => ({ userId, status: 'pending' })),
      createdByUserId: currentUser.id,
      notes: notes.trim() || undefined,
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <Field label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Support Shift" className="input" /></Field>
      <Field label="Type">
        <div className="flex gap-2">
          {(Object.keys(typeMeta) as EventType[]).map((t) => (
            <button key={t} onClick={() => { setType(t); if (t === 'shift' && assigneeIds.length !== 1) setAssigneeIds(assignable[0] ? [assignable[0].id] : []) }} className={`flex-1 rounded-xl border py-2 text-xs font-semibold ${type === t ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-500'}`}>
              {typeMeta[t].icon} {typeMeta[t].label}
            </button>
          ))}
        </div>
      </Field>
      <div className="flex gap-3"><Field label="Date" className="flex-1"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input" /></Field></div>
      <div className="flex gap-3">
        <Field label="Start" className="flex-1"><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="input" /></Field>
        <Field label="End" className="flex-1"><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="input" /></Field>
      </div>

      <div>
        <p className="mb-1 text-xs font-semibold text-slate-600">{type === 'shift' ? 'Assign to' : type === 'meeting' ? 'Invite (optional, pick any number)' : 'Assign to (optional)'}</p>
        {type === 'meeting' ? (
          <div className="flex flex-col gap-1.5">
            {assignable.map((u) => <label key={u.id} className="flex items-center gap-2 rounded-lg px-1 py-1 text-sm text-slate-700"><input type="checkbox" checked={assigneeIds.includes(u.id)} onChange={() => toggleAssignee(u.id)} />{u.name} <span className="text-xs text-slate-400">— {u.title ?? u.role}</span></label>)}
            <p className="mt-0.5 text-[11px] text-slate-400">Tip: for meetings you'll coordinate over time, a group chat lets you discuss and schedule from the same thread.</p>
          </div>
        ) : (
          <select value={assigneeIds[0] ?? ''} onChange={(e) => setAssigneeIds(e.target.value ? [e.target.value] : [])} className="input">
            {type !== 'shift' && <option value="">— Leave unassigned for now —</option>}
            {assignable.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.title ?? u.role}</option>)}
          </select>
        )}
      </div>

      <Field label="Notes (optional)"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input" /></Field>
      <div className="mt-2 flex gap-2">
        <button onClick={onClose} className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-600">Cancel</button>
        <button onClick={submit} className="flex-1 rounded-xl bg-brand-600 py-3 text-sm font-bold text-white">Save & Notify</button>
      </div>
    </div>
  )
}

function Field({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return <label className={`block text-xs font-semibold text-slate-600 ${className}`}><span className="mb-1 block">{label}</span>{children}</label>
}

